/* utils/generateSchema.ts */

type Input = {
  contentType: string;
  meta_title?: string;
  meta_description?: string;
  share_image?: string;
  imageWidth?: number;
  imageHeight?: number;
  publishDate?: string;
  modifiedDate?: string;
  webinarDate?: string;
  authorName?: string;
  canonicalUrl: string;
  baseUrl: string;
  streetAddress?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  telephone?: string;
  locale?: string;
  keywords?: string[];
  videoDuration?: string; // ISO 8601 format, e.g. PT2M33S
};

type JsonLd = Record<string, any>;

const iso = (d?: string) => (d ? new Date(d).toISOString() : undefined);

const arr = (v?: string) => (v ? [v] : undefined);

const compact = <T extends Record<string, any>>(obj: T): T => {
  const out: Record<string, any> = {};
  Object.keys(obj).forEach(k => {
    const v = obj[k];
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v) && v.length === 0) return;
    if (typeof v === 'object' && !Array.isArray(v)) {
      const cleaned = compact(v as Record<string, any>);
      if (Object.keys(cleaned).length === 0) return;
      out[k] = cleaned;
    } else {
      out[k] = v;
    }
  });
  return out as T;
};

export const generateSchema = ({
  contentType,
  meta_title,
  meta_description,
  share_image,
  imageWidth,
  imageHeight,
  publishDate,
  modifiedDate,
  webinarDate,
  authorName,
  canonicalUrl,
  baseUrl,
  streetAddress,
  city,
  region,
  postalCode,
  country,
  latitude,
  longitude,
  telephone,
  locale,
  keywords,
  videoDuration,
}: Input): JsonLd => {
  const imageUrl = share_image || `${baseUrl}/default-share.jpg`;

  const imageObj =
    imageUrl || imageWidth || imageHeight
      ? {
          '@type': 'ImageObject',
          url: imageUrl,
          width: imageWidth,
          height: imageHeight,
        }
      : undefined;

  const commonArticle = compact({
    '@context': 'https://schema.org',
    headline: meta_title,
    description: meta_description,
    image: imageObj ? [imageObj] : undefined,
    author: authorName ? { '@type': 'Organization', name: authorName } : undefined,
    publisher: {
      '@type': 'Organization',
      name: 'Form Technologies',
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/favicon-32x32.png`,
      },
    },
    datePublished: iso(publishDate),
    dateModified: iso(modifiedDate || publishDate),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    inLanguage: locale,
    keywords: keywords && keywords.length > 0 ? keywords.join(', ') : undefined,
  });

  const t = (contentType || 'page').toLowerCase();

  if (t === 'article' || t === 'whitepaper') {
    return {
      '@type': t === 'whitepaper' ? 'TechArticle' : 'Article',
      ...commonArticle,
    };
  }

  if (t === 'casestudy') {
    return {
      '@type': 'Report',
      ...commonArticle,
    };
  }

  if (t === 'webinar' || t === 'video') {
    return compact({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: meta_title,
      description: meta_description,
      url: canonicalUrl,
      primaryImageOfPage: imageObj ? [imageObj] : undefined,
      datePublished: iso(publishDate),
      dateModified: iso(modifiedDate || publishDate),
      ...(t === 'webinar' && webinarDate ? { eventDate: iso(webinarDate) } : {}),
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Form Technologies',
        logo: {
          '@type': 'ImageObject',
          url: `${baseUrl}/favicon-32x32.png`,
        },
      },
      inLanguage: locale,
      keywords: keywords && keywords.length > 0 ? keywords.join(', ') : undefined,
    });
  }

  if (t === 'product') {
    return compact({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: meta_title,
      description: meta_description,
      image: imageObj ? [imageObj] : undefined,
      url: canonicalUrl,
      brand: {
        '@type': 'Organization',
        name: 'Form Technologies',
      },
      inLanguage: locale,
      keywords: keywords && keywords.length > 0 ? keywords.join(', ') : undefined,
    });
  }

  if (t === 'location') {
    return compact({
      '@context': 'https://schema.org',
      '@type': 'Place',
      name: meta_title,
      description: meta_description,
      image: imageObj ? [imageObj] : undefined,
      url: canonicalUrl,
      address:
        streetAddress || city || region || postalCode || country
          ? compact({
              '@type': 'PostalAddress',
              streetAddress,
              addressLocality: city,
              addressRegion: region,
              postalCode,
              addressCountry: country,
            })
          : undefined,
      geo:
        typeof latitude === 'number' && typeof longitude === 'number'
          ? {
              '@type': 'GeoCoordinates',
              latitude,
              longitude,
            }
          : undefined,
      telephone,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
      inLanguage: locale,
      keywords: keywords && keywords.length > 0 ? keywords.join(', ') : undefined,
    });
  }

  // fallback WebPage
  return compact({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: meta_title,
    description: meta_description,
    url: canonicalUrl,
    primaryImageOfPage: imageObj ? [imageObj] : undefined,
    datePublished: iso(publishDate),
    dateModified: iso(modifiedDate || publishDate),
    inLanguage: locale,
    keywords: keywords && keywords.length > 0 ? keywords.join(', ') : undefined,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${baseUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  });
};
